import axios from 'axios';

/**
 * Keys holding the signed-in student's session. Kept in step with the logout path
 * in index.tsx, so an expired session is cleared the same way a deliberate one is.
 */
const SESSION_KEYS = [
  'token',
  'userData',
  'auth_error',
  'lastStartedTest',
  'lastTestStartTime',
  'selectedTest',
  'persist:root',
];

/** True once a redirect is under way, so several failing calls do not each trigger one. */
let redirecting = false;

/**
 * redux-persist writes in-memory state back to storage on a throttle, so clearing
 * localStorage alone is not enough — the token is rewritten before the page navigates
 * and rehydrates on the next load, sending the app straight back to the screen that
 * failed. The persistor is registered from index.tsx rather than imported, to avoid a
 * cycle: store imports slices, which import services, which import this module.
 */
let persistor: { pause: () => void; purge: () => Promise<any> } | null = null;

export const registerPersistor = (instance: typeof persistor): void => {
  persistor = instance;
};

export const clearSession = () => {
  SESSION_KEYS.forEach((key) => localStorage.removeItem(key));
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith('liveolympiad_') || key.startsWith('student_') || key.startsWith('test_')) {
      localStorage.removeItem(key);
    }
  });
  sessionStorage.clear();
};

/**
 * Clears the stored session and returns to sign in. Safe to call more than once:
 * only the first call navigates.
 */
export const endExpiredSession = (): void => {
  // Already on the sign-in screen: nothing to leave, and redirecting would loop.
  if (redirecting || window.location.pathname === '/login') {
    return;
  }
  redirecting = true;

  const leave = () => {
    clearSession();
    window.location.replace('/login');
  };

  if (persistor) {
    persistor.pause();
    persistor.purge().then(leave, leave);
  } else {
    leave();
  }
};

/**
 * Sends the student back to sign in when their token is no longer accepted.
 *
 * Student tokens last 24 hours. Without this, an expired one surfaced as a raw
 * "Request failed with status code 401" on whichever screen happened to make the
 * call, with no way forward but clearing site data by hand.
 */
export const installSessionExpiryHandler = (): void => {
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      const isLoginCall = String(error?.config?.url || '').includes('/auth/login');

      // A 401 from the login call itself means wrong credentials, not an expired session.
      if ((status === 401 || status === 403) && !isLoginCall) {
        endExpiredSession();
      }
      return Promise.reject(error);
    }
  );
};

export default installSessionExpiryHandler;
